/**
 * Where the AI sits on desktop (≥ 1024 px): one layout, two states.
 *
 * - Hidden: the full left menu, with Ask AI at its foot.
 * - Open: the menu shrinks to a 64 px icon column and the AI panel sits right of
 *   it, then the page. "Show full menu" puts the full menu back beside the AI
 *   for the rest of the session.
 *
 * Below 1024 px the AI is the drawer, opened from the header (no change).
 *
 * Until 2026-10 there were three desktop modes (Drawer / Dock / Rail) stored
 * under `assistantLayoutMode`. Those values still load: they are all the side
 * panel now, open for Dock and an expanded Rail, hidden for Drawer and a
 * collapsed Rail.
 */

/** The stored open/hidden choice: `'true'` or `'false'`. */
export const ASSISTANT_OPEN_KEY = 'assistantOpen'

/** Legacy (before 2026-10): `drawer` | `dock` | `rail`. Read once, never written. */
export const LEGACY_ASSISTANT_LAYOUT_KEY = 'assistantLayoutMode'
/** Legacy: `'false'` when the rail was collapsed. Read once, never written. */
export const LEGACY_ASSISTANT_RAIL_EXPANDED_KEY = 'assistantRailExpanded'

/** Session only: the full menu stays beside the open AI instead of the icon column. */
export const ASSISTANT_FULL_MENU_KEY = 'assistantFullMenuWithAi'

/** The panel and the icon column, in px (the classes use the same numbers). */
export const ASSISTANT_PANEL_WIDTH = 380
export const ICON_RAIL_WIDTH = 64

function storage(kind: 'local' | 'session'): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

/**
 * The user's open/hidden choice, or `null` when they have never made one.
 *
 * A pre-2026-10 layout counts as a choice: Dock and an expanded Rail were on
 * screen (open), Drawer started closed and a collapsed Rail was a strip (hidden).
 * Anything unreadable is "no choice".
 */
export function readAssistantOpen(): boolean | null {
  const ls = storage('local')
  if (!ls) return null
  const saved = ls.getItem(ASSISTANT_OPEN_KEY)
  if (saved === 'true') return true
  if (saved === 'false') return false

  const legacy = ls.getItem(LEGACY_ASSISTANT_LAYOUT_KEY)
  if (legacy === 'dock') return true
  if (legacy === 'rail') return ls.getItem(LEGACY_ASSISTANT_RAIL_EXPANDED_KEY) !== 'false'
  if (legacy === 'drawer') return false
  return null
}

export function writeAssistantOpen(open: boolean) {
  storage('local')?.setItem(ASSISTANT_OPEN_KEY, String(open))
}

/**
 * Whether the AI starts open: the stored choice, else open on a platform page
 * (Platform admin opens with the AI the first time), else hidden.
 */
export function initialAssistantOpen(pathname: string): boolean {
  const saved = readAssistantOpen()
  if (saved !== null) return saved
  return isPlatformPath(pathname)
}

export function isPlatformPath(pathname: string): boolean {
  return pathname === '/platform' || pathname.startsWith('/platform/')
}

export function readFullMenuWithAssistant(): boolean {
  return storage('session')?.getItem(ASSISTANT_FULL_MENU_KEY) === 'true'
}

export function writeFullMenuWithAssistant(full: boolean) {
  const ss = storage('session')
  if (!ss) return
  if (full) ss.setItem(ASSISTANT_FULL_MENU_KEY, 'true')
  else ss.removeItem(ASSISTANT_FULL_MENU_KEY)
}
